import React from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { Button, PrimaryButton } from "src/components/base/button";
import {
    GetPendingRoutesResponseItem,
    GetPendingRoutesResponseItemSubnetOneOf1,
} from "src/api/generated/frontend_internal_v1";
import { Text } from "src/components/base/text";
import { Subheading } from "src/components/base/heading";
import { ServerStackIcon } from "@heroicons/react/20/solid";

/**
 * The properties for {@link ApproveSubnetRouterDialog}
 */
export type ApproveSubnetRouterDialogProps = {
    /** The subnet and router to approve */
    route: (GetPendingRoutesResponseItem & { subnet: GetPendingRoutesResponseItemSubnetOneOf1 }) | undefined;
    /** Callback when the form is submitted */
    onSubmit: () => void;
    /** Callback when the dialog is closed */
    onClose: () => void;
};

/**
 * A dialog to approve a new router for an existing subnet
 */
export default function ApproveSubnetRouterDialog(props: ApproveSubnetRouterDialogProps) {
    const [t] = useTranslation("dialog-approve-subnet-router");
    const [tg] = useTranslation();

    if (!props.route) return null;
    const { subnet, device } = props.route;

    return (
        <Dialog open onClose={props.onClose}>
            <DialogTitle>{t("heading.approve-subnet")}</DialogTitle>
            <DialogBody>
                {subnet.routers.length !== 0 && (
                    <>
                        <Text>{t("description.approve-subnet-router")}</Text>
                        <Subheading className={"mt-5 mb-1"}>{t("heading.current-routers")}</Subheading>
                    </>
                )}
                <div className={"flex flex-col gap-1"}>
                    {subnet.routers
                        .sort((a, b) => a.dnsname.localeCompare(b.dnsname))
                        .map((router) => (
                            <div className={"flex gap-1"}>
                                <Button
                                    outline
                                    className={"grow justify-start!"}
                                    href={"/a/devices/$deviceId/general"}
                                    target={"_blank"}
                                    params={{ deviceId: router.uuid }}
                                >
                                    <ServerStackIcon className={"size-5"} />
                                    <span>{router.dnsname}</span>
                                </Button>
                            </div>
                        ))}
                </div>
                <DialogActions>
                    <Button plain={true} onClick={props.onClose}>
                        {tg("button.cancel")}
                    </Button>
                    {subnet.routers.length === 0 ? (
                        <PrimaryButton
                            onClick={async () => {
                                await ApiInternal.subnets.addRouter(subnet.uuid, device.uuid);
                                props.onSubmit();
                            }}
                        >
                            {t("button.approve")}
                        </PrimaryButton>
                    ) : (
                        <>
                            <PrimaryButton
                                onClick={async () => {
                                    await ApiInternal.subnets.addRouter(subnet.uuid, device.uuid);
                                    for (const device of subnet.routers) {
                                        await ApiInternal.subnets.removeRouter(subnet.uuid, device.uuid);
                                    }
                                    props.onSubmit();
                                }}
                            >
                                {t("button.replace-others")}
                            </PrimaryButton>
                            <PrimaryButton
                                onClick={async () => {
                                    await ApiInternal.subnets.addRouter(subnet.uuid, device.uuid);
                                    props.onSubmit();
                                }}
                            >
                                {t("button.add-to-others")}
                            </PrimaryButton>
                        </>
                    )}
                </DialogActions>
            </DialogBody>
        </Dialog>
    );
}
